import { Button, Icon, Link, Modal, ModalActions, ModalContent, ModalWrapper } from '@braskai/ui-kit';
import { useLocation, useNavigate } from 'react-router-dom';

import { Stack, Typography } from '@mui/material';

import { RASK_CHAT_LINK } from '@/shared/constants/docLinks';
import { PATHS } from '@/shared/constants/routes/paths';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { createTextParser } from '@/shared/lib/createTextParser';
import { ModalHeaderSecondary } from '@/shared/ui/modal';

import { DATA_TEST_IDS } from '../../lib/constants';
import { BUTTON_TEXTS } from '../../lib/limitedFeatures/texts';
import { LimitedFeature } from '../../lib/types';
import { useUpgradePlanModalContent } from '../../model/useUpgradePlanModalContent';
import { useUpgradePlanModalStore } from '../../model/useUpgradePlanModalStore';
import { SDescriptionText, SImage, SNativeVideoPlayer } from './styled';

const UpgradePlanModalComponent = ({ isSpaceOwner }: { isSpaceOwner: boolean }) => {
  const limitedFeature = useUpgradePlanModalStore((state) => state.limitedFeature);
  const plan = useUpgradePlanModalStore((state) => state.plan);
  const close = useUpgradePlanModalStore((state) => state.close);

  const navigate = useNavigate();
  const location = useLocation();

  const { content } = useUpgradePlanModalContent(limitedFeature as LimitedFeature, plan);

  const { checkIsSpaceOwner = true } = content ?? {};

  useExecuteOneTime(() => {
    content?.mixpanelOpeningEvent();
  });

  if (!content) {
    console.error('Should show upgrade modal but no content for this plan category was provided');

    return null;
  }

  const {
    title,
    bulletsHeader,
    buttonText,
    caption,
    features,
    media: { poster, src, type: mediaType, videoOptions },
    mixpanelButtonClickEvent,
    subtitle,
  } = content;

  const handleButtonClick = () => {
    mixpanelButtonClickEvent();

    if (buttonText === BUTTON_TEXTS.contactUs) {
      window.open(RASK_CHAT_LINK, '_blank');
      return;
    } else {
      navigate(`/${PATHS.pricing}`, { state: { path: location.pathname } });
    }

    close();
  };

  const parseLinksAndBoldAndGrayText = createTextParser(Link, SDescriptionText);

  return (
    <Modal size="xs" open onClose={close}>
      <ModalHeaderSecondary title={title} subtitle={subtitle} onClose={close} />

      <ModalContent noPadding>
        {mediaType === 'video' ? (
          <SNativeVideoPlayer poster={poster} src={src} {...videoOptions} />
        ) : (
          <SImage $isGif={mediaType === 'gif'} src={src} />
        )}

        <Typography color="grey.700" marginTop={8} variant="caption-1-md">
          {parseLinksAndBoldAndGrayText(caption)}
        </Typography>

        <Typography color="grey.700" component="h3" marginTop={16} variant="body-1-md">
          {bulletsHeader}
        </Typography>

        <Stack gap={12} marginBottom={4} marginTop={12}>
          {features.map(({ description, icon }) => (
            <Stack key={description} direction="row" gap={8}>
              <Icon color="primary.600" component={icon} size={20} />
              <Typography color="grey.700" variant="body-2-md">
                {parseLinksAndBoldAndGrayText(description)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </ModalContent>

      <ModalActions>
        {!IS_WHITE_LABEL && (
          <Button
            data-testid={DATA_TEST_IDS.upgradeButton}
            disabled={checkIsSpaceOwner && !isSpaceOwner}
            size="lg"
            variant="primary"
            fullWidth
            onClick={handleButtonClick}
          >
            {buttonText}
          </Button>
        )}
      </ModalActions>
    </Modal>
  );
};

export const UpgradePlanModal = ({ isSpaceOwner }: { isSpaceOwner: boolean }) => (
  <ModalWrapper hook={useUpgradePlanModalStore}>
    <UpgradePlanModalComponent isSpaceOwner={isSpaceOwner} />
  </ModalWrapper>
);
